import { getSoundPreferences } from "@/lib/audio/preferences";

export type Cue =
  | "join"
  | "leave"
  | "message"
  | "hand"
  | "knock"
  | "admitted"
  | "recordingOn"
  | "recordingOff"
  | "mutedByHost"
  | "speakingWhileMuted"
  | "handOwn"
  | "handOwnDown"
  | "granted"
  | "revoked"
  | "page"
  | "micOn"
  | "micOff"
  | "camOn"
  | "camOff"
  | "shareOn"
  | "shareOff"
  | "present"
  | "reconnecting"
  | "reconnected"
  | "ended"
  | "success"
  | "error"
  | "test";

type Note = {
  pitch: string;

  at: number;

  hold: number;

  level?: number;
  wave?: OscillatorType;
};

type CueSpec = {
  level: number;
  notes: Note[];
};

const SEMITONES: Record<string, number> = { C: -9, D: -7, E: -5, F: -4, G: -2, A: 0, B: 2 };

function hz(pitch: string): number {
  const match = /^([A-G])(#?)(\d)$/u.exec(pitch);
  if (match === null) return 440;
  const [, letter = "A", sharp = "", octave = "4"] = match;
  const semitones = (SEMITONES[letter] ?? 0) + (sharp === "#" ? 1 : 0) + (Number(octave) - 4) * 12;
  return 440 * 2 ** (semitones / 12);
}

const CUES: Record<Cue, CueSpec> = {
  join: {
    level: 0.22,
    notes: [
      { pitch: "G5", at: 0, hold: 0.14 },
      { pitch: "C6", at: 0.09, hold: 0.24 },
    ],
  },
  leave: {
    level: 0.18,
    notes: [
      { pitch: "C6", at: 0, hold: 0.14 },
      { pitch: "G5", at: 0.09, hold: 0.26 },
    ],
  },

  message: {
    level: 0.16,
    notes: [
      { pitch: "D6", at: 0, hold: 0.1 },
      { pitch: "A6", at: 0.05, hold: 0.14, level: 0.5 },
    ],
  },
  hand: {
    level: 0.2,
    notes: [
      { pitch: "C6", at: 0, hold: 0.12 },
      { pitch: "E6", at: 0.07, hold: 0.12 },
      { pitch: "G6", at: 0.14, hold: 0.3 },
    ],
  },

  knock: {
    level: 0.3,
    notes: [
      { pitch: "F3", at: 0, hold: 0.09, wave: "triangle" },
      { pitch: "F3", at: 0.16, hold: 0.11, wave: "triangle" },
    ],
  },
  admitted: {
    level: 0.22,
    notes: [
      { pitch: "C5", at: 0, hold: 0.12 },
      { pitch: "E5", at: 0.07, hold: 0.12 },
      { pitch: "G5", at: 0.14, hold: 0.12 },
      { pitch: "C6", at: 0.21, hold: 0.34 },
    ],
  },
  recordingOn: {
    level: 0.2,
    notes: [
      { pitch: "A4", at: 0, hold: 0.16 },
      { pitch: "E5", at: 0.13, hold: 0.3 },
    ],
  },
  recordingOff: {
    level: 0.2,
    notes: [
      { pitch: "E5", at: 0, hold: 0.16 },
      { pitch: "A4", at: 0.13, hold: 0.3 },
    ],
  },

  mutedByHost: {
    level: 0.26,
    notes: [
      { pitch: "G4", at: 0, hold: 0.16, wave: "triangle" },
      { pitch: "C4", at: 0.1, hold: 0.36, wave: "triangle" },
    ],
  },

  speakingWhileMuted: {
    level: 0.28,
    notes: [
      { pitch: "F#5", at: 0, hold: 0.14 },
      { pitch: "D5", at: 0.13, hold: 0.3 },
    ],
  },

  handOwn: {
    level: 0.16,
    notes: [
      { pitch: "E6", at: 0, hold: 0.1 },
      { pitch: "B6", at: 0.07, hold: 0.2 },
    ],
  },
  handOwnDown: {
    level: 0.14,
    notes: [
      { pitch: "B6", at: 0, hold: 0.1 },
      { pitch: "E6", at: 0.07, hold: 0.18 },
    ],
  },

  granted: {
    level: 0.2,
    notes: [
      { pitch: "C5", at: 0, hold: 0.12 },
      { pitch: "G5", at: 0.09, hold: 0.26 },
    ],
  },
  revoked: {
    level: 0.2,
    notes: [
      { pitch: "G5", at: 0, hold: 0.12 },
      { pitch: "C5", at: 0.09, hold: 0.28 },
    ],
  },

  page: {
    level: 0.1,
    notes: [
      { pitch: "D7", at: 0, hold: 0.05, wave: "triangle" },
      { pitch: "A6", at: 0.03, hold: 0.07, wave: "triangle" },
    ],
  },
  micOn: { level: 0.16, notes: [{ pitch: "A5", at: 0, hold: 0.12 }] },
  micOff: { level: 0.16, notes: [{ pitch: "D5", at: 0, hold: 0.14 }] },
  camOn: { level: 0.14, notes: [{ pitch: "B5", at: 0, hold: 0.11, wave: "triangle" }] },
  camOff: { level: 0.14, notes: [{ pitch: "E5", at: 0, hold: 0.13, wave: "triangle" }] },
  shareOn: {
    level: 0.2,
    notes: [
      { pitch: "D5", at: 0, hold: 0.12 },
      { pitch: "A5", at: 0.08, hold: 0.24 },
    ],
  },
  shareOff: {
    level: 0.18,
    notes: [
      { pitch: "A5", at: 0, hold: 0.12 },
      { pitch: "D5", at: 0.08, hold: 0.24 },
    ],
  },
  present: {
    level: 0.2,
    notes: [
      { pitch: "C5", at: 0, hold: 0.14 },
      { pitch: "G5", at: 0.1, hold: 0.14 },
      { pitch: "C6", at: 0.2, hold: 0.34 },
    ],
  },

  reconnecting: {
    level: 0.22,
    notes: [
      { pitch: "E5", at: 0, hold: 0.16 },
      { pitch: "C5", at: 0.14, hold: 0.16 },
      { pitch: "A4", at: 0.28, hold: 0.36 },
    ],
  },
  reconnected: {
    level: 0.22,
    notes: [
      { pitch: "A4", at: 0, hold: 0.14 },
      { pitch: "C5", at: 0.1, hold: 0.14 },
      { pitch: "E5", at: 0.2, hold: 0.32 },
    ],
  },
  ended: {
    level: 0.24,
    notes: [
      { pitch: "C5", at: 0, hold: 0.3, wave: "triangle" },
      { pitch: "G4", at: 0.16, hold: 0.34, wave: "triangle" },
      { pitch: "C4", at: 0.32, hold: 0.6, wave: "triangle" },
    ],
  },

  success: {
    level: 0.16,
    notes: [
      { pitch: "E5", at: 0, hold: 0.1 },
      { pitch: "B5", at: 0.07, hold: 0.2 },
    ],
  },
  error: {
    level: 0.22,
    notes: [
      { pitch: "A3", at: 0, hold: 0.14, wave: "triangle" },
      { pitch: "G#3", at: 0.11, hold: 0.28, wave: "triangle" },
    ],
  },
  test: {
    level: 0.24,
    notes: [
      { pitch: "C5", at: 0, hold: 0.13 },
      { pitch: "E5", at: 0.1, hold: 0.13 },
      { pitch: "G5", at: 0.2, hold: 0.36 },
    ],
  },
};

type Routable = AudioContext & { setSinkId?: (id: string) => Promise<void> };

let context: Routable | null = null;
let master: GainNode | null = null;
let sink = "";

const MIN_GAP_MS = 220;
const lastPlayed = new Map<Cue, number>();

function graph(): { context: Routable; master: GainNode } | null {
  if (context === null) {
    const Ctor: typeof AudioContext | undefined =
      globalThis.AudioContext ??
      (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctor === undefined) return null;
    try {
      context = new Ctor();
    } catch {
      return null;
    }
    master = context.createGain();
    master.gain.value = getSoundPreferences().volume;
    master.connect(context.destination);
    if (sink !== "") void context.setSinkId?.(sink).catch(() => undefined);
  }
  return master === null ? null : { context, master };
}

export function unlockAudio(): void {
  const built = graph();
  if (built === null) return;
  if (built.context.state === "suspended") void built.context.resume().catch(() => undefined);
}

export function setCueOutput(deviceId: string): void {
  sink = deviceId;
  if (context === null || deviceId === "") return;
  void context.setSinkId?.(deviceId).catch(() => undefined);
}

export function setCueVolume(volume: number): void {
  if (master === null || context === null) return;
  master.gain.setTargetAtTime(volume, context.currentTime, 0.01);
}

export function playCue(cue: Cue, options?: { force?: boolean }): void {
  const preferences = getSoundPreferences();

  if ((!preferences.cues && options?.force !== true) || preferences.volume === 0) return;

  const now = Date.now();
  if (now - (lastPlayed.get(cue) ?? 0) < MIN_GAP_MS) return;
  lastPlayed.set(cue, now);

  const built = graph();
  if (built === null) return;
  const { context: ctx, master: out } = built;
  if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
  out.gain.value = preferences.volume;

  const spec = CUES[cue];
  const start = ctx.currentTime + 0.01;
  for (const note of spec.notes) {
    const oscillator = ctx.createOscillator();
    oscillator.type = note.wave ?? "sine";
    oscillator.frequency.value = hz(note.pitch);

    const envelope = ctx.createGain();
    const peak = spec.level * (note.level ?? 1);
    const at = start + note.at;

    envelope.gain.setValueAtTime(0.0001, at);
    envelope.gain.exponentialRampToValueAtTime(peak, at + 0.008);
    envelope.gain.exponentialRampToValueAtTime(0.0001, at + note.hold);

    oscillator.connect(envelope);
    envelope.connect(out);
    oscillator.start(at);
    oscillator.stop(at + note.hold + 0.05);
    oscillator.addEventListener(
      "ended",
      () => {
        oscillator.disconnect();
        envelope.disconnect();
      },
      { once: true },
    );
  }
}
